(() => {
  if (customElements.get('nn-naked-line')) return;

  const terms = value => (value || '').split(',').map(term => term.trim()).filter(Boolean);
  const normalize = value => (value || '').toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ß/g, 'ss');

  class NakedLine extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.scope = this.hasAttribute("data-modular") ? (this.closest("main") || document) : this;
      this.cards = [...this.scope.querySelectorAll('[data-catalog-grid] [data-product]')];
      this.category = 'all';
      this.occasion = '';
      this.search = this.scope.querySelector('[data-search-input]');
      this.form = this.scope.querySelector('[data-finder]');
      this.scope.querySelector('[data-toolbar]')?.removeAttribute('hidden');
      this.scope.querySelector('[data-search-control]')?.removeAttribute('hidden');
      if (this.form) this.form.hidden = false;
      this.scope.querySelector('[data-ritual-controls]')?.removeAttribute('hidden');
      this.showRitual('breakfast');
      this.filter();
      document.fonts.ready.then(() => { if (this.isConnected) this.alignCards(); });
      this.gridWidths = new WeakMap();
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(entries => {
        if (entries.some(entry => this.gridWidths.get(entry.target) !== entry.contentRect.width)) {
          entries.forEach(entry => this.gridWidths.set(entry.target, entry.contentRect.width));
          this.alignCards();
        }
      });
      this.scope.querySelectorAll('[data-catalog-grid], [data-result-grid]').forEach(grid => this.resizeObserver.observe(grid));

      this.search?.addEventListener('input', () => this.filter(), options);
      this.form?.addEventListener('submit', event => {
        event.preventDefault();
        this.recommend();
      }, options);
      this.form?.addEventListener('change', () => {
        this.scope.querySelector('[data-results]').hidden = true;
      }, options);
      this.scope.addEventListener('click', event => {
        const button = event.target.closest('button, a');
        if (!button || !this.scope.contains(button)) return;
        if (button.hasAttribute('data-product-link')) {
          event.preventDefault();
          this.reset();
          const product = this.cards.find(card => card.dataset.handle === button.dataset.productLink);
          if (this.search) this.search.value = product?.querySelector('.product-card__title')?.textContent.trim() || '';
          this.filter();
          this.goTo(product || this.catalogTarget());
        }
        if (button.hasAttribute('data-filter')) {
          this.category = button.dataset.filter;
          this.occasion = '';
          this.filter();
        }
        if (button.hasAttribute('data-category-link')) {
          event.preventDefault();
          this.reset();
          if (this.hasAttribute('data-modular') && !this.scope.querySelector('[data-filter]')) {
            this.goTo(this.scope.querySelector('[data-category-section="' + button.dataset.categoryLink + '"]'));
            return;
          }
          this.category = button.dataset.categoryLink;
          this.filter();
          this.goTo(this.catalogTarget());
        }
        if (button.hasAttribute('data-occasion-link')) {
          event.preventDefault();
          this.reset();
          if (this.hasAttribute('data-modular') && !this.scope.querySelector('[data-filter]')) {
            this.goTo(this.cards.find(card => terms(card.dataset.occasions).includes(button.dataset.occasionLink)) || this.catalogTarget());
            return;
          }
          this.occasion = button.dataset.occasionLink;
          this.filter();
          this.goTo(this.catalogTarget());
        }
        if (button.hasAttribute('data-finder-link')) {
          event.preventDefault();
          this.goTo(this.form?.querySelector('input[name="occasion"]'));
        }
        if (button.hasAttribute('data-reset')) {
          this.reset();
          (this.search || this.scope.querySelector('[data-filter="all"]'))?.focus({ preventScroll: true });
        }
        if (button.hasAttribute('data-ritual')) this.showRitual(button.dataset.ritual);
        if (button.hasAttribute('data-restart')) {
          this.form.reset();
          this.scope.querySelector('[data-results]').hidden = true;
          this.goTo(this.form?.querySelector('input[name="occasion"]'));
        }
      }, options);
    }

    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); cancelAnimationFrame(this.alignmentFrame); }

    catalogTarget() {
      return [...this.scope.querySelectorAll('[data-catalog-grid]')].find(grid => !grid.closest('[data-category-section]')?.hidden);
    }

    goTo(element) {
      if(!element) return;
      element.focus({ preventScroll: true });
      const target = element.closest('section') || element;
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    }

    reset() {
      this.category = 'all';
      this.occasion = '';
      if(this.search) this.search.value = '';
      this.filter();
    }

    filter() {
      const query = normalize(this.search?.value.trim() || '');
      let count = 0;
      for (const card of this.cards) {
        const matches = (this.category === 'all' || card.dataset.category === this.category)
          && (!this.occasion || terms(card.dataset.occasions).includes(this.occasion))
          && (!query || normalize(card.dataset.search).includes(query));
        card.hidden = !matches;
        if (matches) count++;
      }
      this.scope.querySelectorAll('[data-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.filter === this.category)));
      const occasionLabel = this.occasion ? this.scope.querySelector(`[data-ritual="${this.occasion}"]`)?.textContent : '';
      this.scope.querySelectorAll('[data-category-section]').forEach(group => { group.hidden = ![...group.querySelectorAll('[data-product]')].some(card => !card.hidden); });
      this.scope.querySelectorAll('[data-explainer-category]').forEach(group => {
        const category = this.scope.querySelector('[data-category-section="' + group.dataset.explainerCategory + '"]');
        group.hidden = category ? category.hidden : this.category !== 'all' && this.category !== group.dataset.explainerCategory;
      });
      if(this.scope.querySelector('[data-count]')) this.scope.querySelector('[data-count]').textContent = this.dataset.countTemplate.replace('[count]', count) + (occasionLabel ? ` · ${occasionLabel}` : '');
      if(this.scope.querySelector('[data-empty]')) this.scope.querySelector('[data-empty]').hidden = count !== 0;
      if(this.scope.querySelector('[data-reset]')) this.scope.querySelector('[data-reset]').hidden = this.category === 'all' && !this.occasion && !query;
      this.alignCards();
    }

    recommend() {
      const values = new FormData(this.form);
      const occasion = values.get('occasion');
      if (!occasion) return;
      const flavour = values.get('flavour');
      // Own editor-configurable cards keep recommendations independent of visible grids.
      const finderSource = this.querySelector?.('[data-finder-source]');
      const sourceCards = finderSource?.content
        ? [...finderSource.content.querySelectorAll('[data-product]')]
        : [...this.scope.querySelectorAll('[data-finder-source] [data-product]')];
      const cards = sourceCards.length ? sourceCards : this.cards;
      const eligible = cards.filter(card => terms(card.dataset.occasions).includes(occasion))
        .sort((a, b) => Number(terms(b.dataset.preferred).includes(occasion)) - Number(terms(a.dataset.preferred).includes(occasion)));
      const matches = eligible.filter(card => flavour === 'any' || terms(card.dataset.flavours).includes(flavour));
      const chosen = (matches.length ? matches : eligible).slice(0, 3);
      const occasionLabel = this.form.querySelector('input[name="occasion"]:checked').closest('label').textContent.replace('↗', '').trim();
      const flavourLabel = this.form.querySelector('input[name="flavour"]:checked').closest('label').textContent.trim();
      const grid = this.scope.querySelector('[data-result-grid]');
      grid.replaceChildren(...chosen.map(card => {
        const clone = card.cloneNode(true);
        clone.hidden = false;
        clone.removeAttribute('data-shopify-editor-block');
        // Native quick-buy drawers and forms need distinct IDs in recommendations.
        const ids = new Map();
        clone.querySelectorAll('[id]').forEach(element => {
          const previous = element.getAttribute('id');
          const next = `${previous}-recommendation`;
          element.setAttribute('id', next);
          ids.set(previous, next);
        });
        clone.querySelectorAll('[aria-controls], [aria-labelledby], [aria-describedby], [for], [form]').forEach(element => {
          for (const attribute of ['aria-controls', 'aria-labelledby', 'aria-describedby', 'for', 'form']) {
            if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(' ').map(id => ids.get(id) || id).join(' '));
          }
        });
        clone.querySelectorAll('details').forEach(details => { details.open = false; });
        const advice = document.createElement('div');
        advice.className = 'nl-finder-advice';
        const reason = document.createElement('p');
        reason.textContent = (matches.length ? this.dataset.reasonTemplate : this.dataset.reasonFallback).replace('[occasion]', occasionLabel).replace('[taste]', card.dataset.taste);
        const tip = document.createElement('p');
        const label = document.createElement('strong');
        label.textContent = this.dataset.tipLabel;
        tip.append(label, document.createTextNode(card.dataset.usage));
        advice.append(reason, tip);
        const info = clone.querySelector('.product-card__info');
        info.insertBefore(advice, info.querySelector('details'));
        return clone;
      }));
      const explanation = chosen.length
        ? (matches.length ? this.dataset.matchLabel : this.dataset.fallbackLabel)
        : this.dataset.emptyLabel;
      this.scope.querySelector('[data-recommendation-message]').textContent = `${occasionLabel} · ${flavourLabel}. ${explanation}`;
      this.scope.querySelector('[data-results]').hidden = false;
      this.alignCards();
      const heading = this.scope.querySelector('[data-results-title]');
      heading.focus({ preventScroll: true });
      heading.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    }

    alignCards() {
      cancelAnimationFrame(this.alignmentFrame);
      this.alignmentFrame = requestAnimationFrame(() => {
        this.scope.querySelectorAll('[data-catalog-grid], [data-result-grid]').forEach(grid => {
          if (!grid.getClientRects().length) return;
          const cards = [...grid.querySelectorAll('[data-product]')].filter(card => !card.hidden);
          cards.forEach(card => { card.style.removeProperty('--nl-taste-height'); card.style.removeProperty('--nl-title-height'); card.style.removeProperty('--nl-advice-height'); });
          const columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length;
          for (let start = 0; start < cards.length; start += columns) {
            const row = cards.slice(start, start + columns);
            const tasteHeight = Math.max(...row.map(card => card.querySelector('.nl-card__taste')?.getBoundingClientRect().height || 0));
            const titleHeight = Math.max(...row.map(card => card.querySelector('.product-card__title')?.getBoundingClientRect().height || 0));
            const adviceHeight = Math.max(...row.map(card => card.querySelector('.nl-finder-advice')?.getBoundingClientRect().height || 0));
            row.forEach(card => { card.style.setProperty('--nl-taste-height', `${tasteHeight}px`); card.style.setProperty('--nl-title-height', `${titleHeight}px`); card.style.setProperty('--nl-advice-height', `${adviceHeight}px`); });
          }
        });
      });
    }

    showRitual(ritual) {
      this.scope.querySelectorAll('[data-ritual]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ritual === ritual)));
      this.scope.querySelectorAll('[data-ritual-panel]').forEach(panel => { panel.hidden = panel.dataset.ritualPanel !== ritual; });
    }
  }

  customElements.define('nn-naked-line', NakedLine);
})();
